import React, { useState } from "react";
import { X, Share2, Copy, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * ShareFindingModal
 *
 * Renter must explicitly agree to the four-point disclaimer before the
 * "Create link" button enables. Anonymise defaults ON. The renter has to
 * untick it deliberately, and we surface a stronger warning if they do.
 *
 * Wording is the legally significant bit. Do NOT change without product
 * owner sign-off, the exact text is stored on each share record server-side.
 */
export default function ShareFindingModal({ doc, open, onClose }) {
  const [anonymise, setAnonymise] = useState(true);
  const [indexable, setIndexable] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const reset = () => {
    setAgreed(false);
    setAnonymise(true);
    setIndexable(false);
    setResult(null);
    setBusy(false);
  };
  const close = () => { reset(); onClose?.(); };

  const submit = async () => {
    if (!agreed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/findings-share`, {
        anonymise,
        indexable,
        agreed_to_disclaimer: true,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create share link.");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!result?.url) return;
    await navigator.clipboard.writeText(result.url);
    toast.success("Link copied");
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-finding-modal" onClick={close}>
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#1B6C62]/30 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E4DE]">
          <div className="flex items-center gap-2">
            <Share2 size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
            <span className="font-medium text-[#0D2B52]">Share Compass&apos;s findings</span>
          </div>
          <button onClick={close} className="text-[#6B7280] hover:text-[#0D2B52]" data-testid="share-modal-close">
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        {result ? (
          /* Success state, show the URL */
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-2 p-3 bg-[#E6F5F2] border-l-2 border-[#1B6C62]">
              <ShieldCheck size={14} strokeWidth={1.75} className="text-[#1B6C62] mt-0.5" />
              <div className="text-sm text-[#0D2B52] font-serif">
                Your share link is ready. Anyone with this link can read the {result.anonymise ? "anonymised " : ""}analysis without logging in.
              </div>
            </div>

            <div className="flex items-center gap-2 p-2.5 bg-[#FAF9F6] border border-[#E5E4DE]">
              <code className="text-xs text-[#0D2B52] flex-1 truncate" data-testid="share-result-url">{result.url}</code>
              <button onClick={copyUrl} className="btn-primary text-[11px] inline-flex items-center gap-1.5" data-testid="share-result-copy">
                <Copy size={11} strokeWidth={1.75} /> Copy
              </button>
            </div>

            <p className="text-xs text-[#6B7280] font-serif leading-snug">
              You have {result.active_shares} of {result.active_share_cap} active share links on your plan. Manage them in your account settings.
            </p>

            <button onClick={close} className="w-full btn-outline text-xs">Done</button>
          </div>
        ) : (
          /* Disclaimer + options */
          <div className="p-5 space-y-4">
            <p className="text-sm text-[#1A1A1A] font-serif leading-relaxed">
              You can share Compass&apos;s analysis of <strong>{doc.original_name || "this document"}</strong> as a public read-only page. The original document itself is never published.
            </p>

            {/* Anonymise toggle */}
            <label className="flex items-start gap-2.5 p-3 bg-[#FAF9F6] border border-[#E5E4DE] cursor-pointer" data-testid="share-anon-toggle">
              <input
                type="checkbox"
                checked={anonymise}
                onChange={e => setAnonymise(e.target.checked)}
                className="mt-0.5"
              />
              <div className="text-[13px] text-[#1A1A1A] font-serif leading-snug">
                <div className="font-medium text-[#0D2B52]">Anonymise the analysis <span className="text-[10px] font-mono text-[#1B6C62] uppercase tracking-wider ml-1">recommended</span></div>
                <div className="text-[12.5px] text-[#5a6b7a] mt-0.5">Replace names of parties with role labels. Strip email addresses, phone numbers and signature lines.</div>
              </div>
            </label>

            {!anonymise && (
              <div className="flex items-start gap-2 p-3 bg-[#FFF7F2] border-l-2 border-[#B35D4C]" data-testid="share-anon-off-warning">
                <AlertTriangle size={13} strokeWidth={1.75} className="text-[#B35D4C] mt-0.5 flex-shrink-0" />
                <div className="text-[12px] text-[#8a3d2c] font-serif leading-snug">
                  You are choosing to publish names and identifying details exactly as they appear in the analysis. You are responsible for any defamation risk this creates.
                </div>
              </div>
            )}

            {/* Indexable toggle (default OFF for privacy) */}
            <label className="flex items-start gap-2.5 p-3 bg-[#FAF9F6] border border-[#E5E4DE] cursor-pointer" data-testid="share-indexable-toggle">
              <input
                type="checkbox"
                checked={indexable}
                onChange={e => setIndexable(e.target.checked)}
                className="mt-0.5"
              />
              <div className="text-[13px] text-[#1A1A1A] font-serif leading-snug">
                <div className="font-medium text-[#0D2B52]">Allow this report to appear in Google search</div>
                <div className="text-[12.5px] text-[#5a6b7a] mt-0.5">Other renters searching for the same kind of clause may find this report. Off by default. We recommend keeping anonymise on if you tick this.</div>
              </div>
            </label>

            {/* Disclaimer block */}
            <div className="bg-[#FAF6E8] border-l-2 border-[#C9A961] p-3 space-y-2">
              <div className="label-mono text-[10px] text-[#8A6D3B]">Before you share</div>
              <ul className="space-y-1.5 text-[12.5px] text-[#1A1A1A] font-serif">
                <li>• You are sharing Compass&apos;s analysis of a document <strong>you</strong> uploaded.</li>
                <li>• You have removed or redacted anything you don&apos;t want public, including names, addresses and signatures.</li>
                <li>• You are the author of this share. TenantTrail is the platform, not the publisher. We do not verify or endorse the contents.</li>
                <li>• You will not use this feature to defame a named individual or company.</li>
              </ul>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="share-agree-toggle">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-[13px] text-[#0D2B52] font-serif">I understand and agree.</span>
            </label>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E4DE]">
              <button onClick={close} className="text-xs text-[#6B7280] px-3 py-2 hover:text-[#0D2B52]" data-testid="share-cancel">Cancel</button>
              <button
                onClick={submit}
                disabled={!agreed || busy}
                className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="share-submit"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Share2 size={11} strokeWidth={1.75} />}
                Create the link
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
